import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaImage, FaShoppingCart, FaSlidersH } from 'react-icons/fa';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { formatPrice } from '@/lib/format';
import { mediaUrl } from '@/lib/media';

/**
 * Kartica proizvoda u mreži.
 * `fromPrice` znači da varijante imaju različite cene → prikaz „od X RSD".
 * Proizvod sa varijantama se ne može dodati odavde jer kupac prvo mora da izabere
 * varijantu — dugme ga tada vodi na stranicu proizvoda.
 */
const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const navigate = useNavigate();

  const handleAdd = () => {
    if (product.hasVariants) {
      navigate(`/proizvod/${product.slug}`);
      return;
    }
    addItem({
      productId: product.id,
      variantId: null,
      slug: product.slug,
      name: product.name,
      price: Number(product.price),
      quantity: 1,
      imageUrl: product.imageUrl || null,
    });
    toast.success(`„${product.name}" je dodat u korpu.`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: Math.min(index, 6) * 0.05 }}
      className="flex flex-col h-full"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/proizvod/${product.slug}`} className="group block">
        <div className="relative aspect-[4/3] overflow-hidden bg-neutral-50 mb-4">
          {product.imageUrl ? (
            <img
              src={mediaUrl(product.imageUrl)}
              alt={product.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-200">
              <FaImage size={40} />
            </div>
          )}
        </div>
        <h3 className="font-heading text-lg text-brand-primary mb-1 group-hover:text-brand-accent transition-colors">
          {product.name}
        </h3>
        <p className="text-brand-accent font-medium">
          {formatPrice(product.price, { from: product.fromPrice })}
        </p>
      </Link>

      {/* Dugme je van Link-a — inače bi klik na njega otvarao i stranicu proizvoda. */}
      <div className="mt-4 pt-1 mt-auto">
        <button
          type="button"
          onClick={handleAdd}
          className={`w-full inline-flex items-center justify-center gap-2 text-sm ${
            product.hasVariants ? 'btn-secondary' : 'btn-accent'
          }`}
          data-testid={`add-to-cart-${product.slug}`}
        >
          {product.hasVariants ? (
            <>
              <FaSlidersH size={13} />
              Izaberite varijantu
            </>
          ) : (
            <>
              <FaShoppingCart size={14} />
              Dodaj u korpu
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default ProductCard;
